package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ActivityDto
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.site.components.automation.jobReceiptHref
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LDivider
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LHistoryIcon
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.pages.app.activityLabel
import dev.lingvo.site.pages.app.formatRelativeTime
import kotlinx.serialization.json.JsonPrimitive
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun ActivitySection(
    activity: List<ActivityDto>,
    project: ProjectDetailDto,
    firstTarget: String?,
    dataPath: String,
    canEditStructure: Boolean,
    onNavigate: (String) -> Unit,
) {
    Column(Modifier.fillMaxWidth().gap(Space.LG)) {
        SectionHeading(
            title = t("app.projects.slug.project_overview_activity_section.recent_activity", "Recent activity"),
            icon = { LHistoryIcon() },
        )
        if (activity.isEmpty()) {
            LEmptyState(
                title = t("app.projects.slug.project_overview_activity_section.no_project_activity_yet", "No project activity yet"),
                description = t("app.projects.slug.project_overview_activity_section.import_a_file_or_begin_translating", "Import a file or begin translating to create the first entry."),
                modifier = Modifier.fillMaxWidth(),
                icon = LEmptyStateIcon.REFRESH,
            ) {
                // Branches on keys, not languages. A project with German and no strings read
                // "Start translating" — directly beneath its own description offering both — and
                // routed into an empty editor. Having a target language says nothing about whether
                // there is anything to translate.
                if (canEditStructure || (project.keyCount > 0 && firstTarget != null)) {
                    LButton(
                        label = if (project.keyCount == 0 || firstTarget == null) t("app.projects.slug.project_overview_activity_section.import_a_file", "Import a file") else t("app.projects.slug.project_overview_activity_section.start_translating", "Start translating"),
                        onClick = {
                            onNavigate(
                                if (project.keyCount == 0) {
                                    dataPath
                                } else {
                                    firstTarget?.let { "/app/projects/${project.slug}/translate/$it" }
                                        ?: dataPath
                                },
                            )
                        },
                        variant = LButtonVariant.SECONDARY,
                    )
                }
            }
        } else {
            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth()) {
                    activity.forEachIndexed { index, item ->
                        ActivityRow(item, activityHref(project.slug, item))
                        if (index != activity.lastIndex) LDivider()
                    }
                }
            }
            // No project-scoped activity view exists to link to — `/app/activity` covers every
            // project in the workspace, which is a different question than this section answers.
            // Stated rather than silent: activity 13 is otherwise unreachable from this page and
            // nothing said so.
            if (activity.size >= ACTIVITY_ROW_CAP) {
                Span(
                    Modifier
                        .color(ColorMode.current.toSitePalette().textMuted)
                        .fontSize(TypeScale.Small)
                        .toAttrs(),
                ) { Text(tPlural("app.projects.slug.project_overview_activity_section.showing_last", ACTIVITY_ROW_CAP, "Showing the last change.", "Showing the last {count} changes.")) }
            }
        }
    }
}

/**
 * One change, and the way to what it changed.
 *
 * The row used to be three spans and nothing else: "Translation approved · German · 2h ago" with
 * no way to reach the string it is about, so the reader went back to the keys list and searched for
 * something the feed already knew the id of. A row with no derivable destination stays plain rather
 * than pretending to be pressable.
 */
@Composable
private fun ActivityRow(item: ActivityDto, href: String?) {
    if (href == null) {
        Div(Modifier.fillMaxWidth().toAttrs()) { ActivityRowBody(item) }
    } else {
        // The whole row is the target: its text — what happened, in which language, how long ago —
        // is the link's accessible name, which is the sentence a reader would have read anyway.
        Link(
            path = href,
            modifier = ProjectOverviewActivityRowStyle.toModifier().fillMaxWidth(),
        ) {
            ActivityRowBody(item)
        }
    }
}

@Composable
private fun ActivityRowBody(item: ActivityDto) {
    Row(
        Modifier.fillMaxWidth().gap(Space.MD).padding(topBottom = Space.MD),
        // **Top, not centre.** These rows run to two or three lines, and centring the glyph against
        // the whole block floated it between "Comment added" and "5 min ago" on the short rows and
        // beside "German" on the long one — five rows down the card, no glyph level with the title
        // it marks. The nudge is the optical alignment `LAlert` already uses for the same shape.
        verticalAlignment = Alignment.Top,
    ) {
        Span(
            Modifier
                .color(ColorMode.current.toSitePalette().accent)
                .padding(top = Space.XXS)
                .toAttrs { attr("aria-hidden", "true") },
        ) {
            LHistoryIcon()
        }
        Column(Modifier.flexGrow(1).gap(Space.XS)) {
            Span(Modifier.fontSize(TypeScale.BodySmall).fontWeight(600).toAttrs()) {
                Text(activityLabel(item.type))
            }
            activityDetail(item.type, item.payload)?.let { detail ->
                Span(
                    Modifier
                        .color(ColorMode.current.toSitePalette().textSecondary)
                        .fontSize(TypeScale.Small)
                        .toAttrs(),
                ) { Text(detail) }
            }
            Span(
                Modifier
                    .color(ColorMode.current.toSitePalette().textMuted)
                    .fontSize(TypeScale.Small)
                    .toAttrs(),
            ) {
                Text(formatRelativeTime(item.createdAt))
            }
        }
    }
}

/**
 * A whole row that is pressable, without three lines of underline.
 *
 * `PlainLinkStyle` underlines on hover, which is right for a counter and wrong for a block of three
 * stacked lines — the row would grow three underlines at once. The ground the table rows already
 * use says the same thing once, and the colour is inherited so the label, the detail and the time
 * keep the three weights that tell them apart.
 */
val ProjectOverviewActivityRowStyle = CssStyle {
    base {
        Modifier
            .styleModifier { property("color", "inherit") }
            .textDecorationLine(TextDecorationLine.None)
            .borderRadius(Radius.Control)
            .transitionColors()
    }
    hover { Modifier.backgroundColor(colorMode.toSitePalette().accentSubtle) }
}

/**
 * Where a row goes, read off the payload the server already writes.
 *
 * Derived from the ids in the payload rather than from a table of types, so an event type nobody
 * has taught this function about is a plain row rather than a broken link. Two rules are stated by
 * type because the id alone would point at the wrong thing:
 *
 * - a deleted key has no permalink to open — the row leads to the list it can be restored from;
 * - a deleted file has nowhere at all to lead, so it stays plain.
 *
 * Machine-translation rows lead to the run's receipt, which is where the credits are accounted for,
 * and a row that names only a language leads to that language in the editor.
 */
internal fun activityHref(slug: String, item: ActivityDto): String? {
    fun text(key: String): String? =
        (item.payload[key] as? JsonPrimitive)?.takeIf { it.isString }?.content?.takeIf(String::isNotBlank)

    fun keysInFile(fileId: String) = "/app/projects/$slug/keys?fileId=$fileId"

    return when (item.type) {
        "KEY_DELETED" -> "/app/projects/$slug/keys/deleted"
        "FILE_DELETED" -> null
        else -> text("keyId")?.let { "/app/projects/$slug/strings/$it" }
            ?: text("jobId")?.let { jobReceiptHref(slug, it) }
            ?: text("fileId")?.let(::keysInFile)
            ?: text("locale")?.let { "/app/projects/$slug/translate/$it" }
    }
}
